import { useEffect, useRef, useState } from 'react';

/**
 * Settle delay (ms) after a preview iframe fires its `load` event before we reveal
 * it. The casino preview is an SPA: `load` only means the shell document arrived,
 * after which it lazily fetches game tiles / art and reflows. Holding the reveal a
 * beat past `load` lets those late assets settle, so the fade-in catches a painted,
 * stable page instead of a half-built one popping into place.
 */
export const IFRAME_SETTLE_MS = 2000;

/**
 * Floor for the over-size gutter, in CSS px. Measuring the *host* scrollbar gives 0
 * on macOS (overlay scrollbars), but the cross-origin casino site can still FORCE a
 * visible scrollbar via its own `::-webkit-scrollbar` CSS that we can neither read
 * nor style. We over-size by at least this much so that forced bar is pushed into
 * the clipped overflow too. Generous enough to cover typical custom scrollbars
 * (~6-16px); the trade-off is up to this many px of the iframe's right edge are
 * clipped on systems that draw no scrollbar at all (usually just margin).
 */
const SCROLLBAR_GUTTER_FALLBACK = 17;

/**
 * The amount (CSS px) to over-size a preview iframe by so its scrollbar is hidden.
 * Takes the larger of the host's classic scrollbar width (exact on Windows/Linux or
 * macOS "always show") and {@link SCROLLBAR_GUTTER_FALLBACK} (covers macOS overlay +
 * a site-forced custom scrollbar). Measured once and cached.
 *
 * The over-sized strip (carrying the un-stylable scrollbar) falls outside an
 * `overflow: hidden` clip container and is hidden, while the page still lays out at
 * the intended width and the wheel still scrolls.
 */
let cachedGutter: number | null = null;
function measureScrollbarGutter(): number {
  if (cachedGutter != null) return cachedGutter;
  if (typeof document === 'undefined' || !document.body) return SCROLLBAR_GUTTER_FALLBACK;
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll;visibility:hidden;';
  document.body.appendChild(probe);
  const measured = probe.offsetWidth - probe.clientWidth;
  probe.remove();
  cachedGutter = Math.max(measured, SCROLLBAR_GUTTER_FALLBACK);
  return cachedGutter;
}

/**
 * Hook form of the scrollbar-gutter measurement. Re-measures once after mount in
 * case `document.body` was not ready during the first render.
 */
export function useScrollbarGutter(): number {
  const [gutter, setGutter] = useState(measureScrollbarGutter);
  useEffect(() => setGutter(measureScrollbarGutter()), []);
  return gutter;
}

/**
 * Gates the fade-in of a preview iframe: `revealed` flips true only once the iframe
 * has fired `load` AND {@link IFRAME_SETTLE_MS} has since elapsed. Wire `onLoad` to
 * the iframe and drive its `opacity` from `revealed`. Pass the iframe `src` (or any
 * per-load key) as `resetKey` so a reload re-gates the fade from scratch.
 */
export function useIframeReveal(resetKey?: unknown): { revealed: boolean; onLoad: () => void } {
  const [revealed, setRevealed] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    // A new source must hide the iframe again until its own load + settle window.
    setRevealed(false);
    return () => window.clearTimeout(timer.current);
  }, [resetKey]);

  const onLoad = (): void => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setRevealed(true), IFRAME_SETTLE_MS);
  };

  return { revealed, onLoad };
}
